import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Layout } from '@/components/Layout';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { Input } from '../components/ui/input';
import { Alert, AlertDescription } from '../components/ui/alert';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Badge } from '../components/ui/badge';
import { Checkbox } from '../components/ui/checkbox';
import { useAuth } from '../contexts/AuthContext';
import { Loader2, Send, Users, AlertTriangle, UserCheck, Search } from 'lucide-react';
import { toast } from 'sonner';

export default function MarketingSmsPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [message, setMessage] = useState('');
  const [encoding, setEncoding] = useState('plain');
  const [campaignName, setCampaignName] = useState('');
  const [selectionMode, setSelectionMode] = useState('group'); // 'group' or 'individual'
  const [selectedGroup, setSelectedGroup] = useState('all');
  const [patientGroups, setPatientGroups] = useState([]);
  const [allPatients, setAllPatients] = useState([]);
  const [selectedPatientIds, setSelectedPatientIds] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [credits, setCredits] = useState(0);
  const [loading, setLoading] = useState(false);
  const [loadingGroups, setLoadingGroups] = useState(false);
  const [loadingPatients, setLoadingPatients] = useState(false);
  const [alertMessage, setAlertMessage] = useState('');
  const [alertType, setAlertType] = useState('');
  const [showConfirmDialog, setShowConfirmDialog] = useState(false);
  const [previewData, setPreviewData] = useState(null);
  const [appendFooter, setAppendFooter] = useState(true); // Append clinic name by default
  const [clinicShortName, setClinicShortName] = useState('');
  const [editingShortName, setEditingShortName] = useState(false);

  const API_URL = process.env.REACT_APP_BACKEND_URL;

  useEffect(() => {
    loadCredits();
    loadPatientGroups();
    loadClinicShortName();
    if (selectionMode === 'individual') {
      loadAllPatients();
    }
  }, [selectionMode]);

  const loadCredits = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sms/credits`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setCredits(data.sms_credits || 0);
      }
    } catch (error) {
      console.error('Failed to load credits:', error);
    }
  };


  const loadClinicShortName = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const userData = await response.json();
        if (userData.clinic_id) {
          const clinicResponse = await fetch(`${API_URL}/api/clinics/${userData.clinic_id}`, {
            headers: { 'Authorization': `Bearer ${token}` }
          });
          
          if (clinicResponse.ok) {
            const clinicData = await clinicResponse.json();
            setClinicShortName(clinicData.sms_short_name || '');
          }
        }
      }
    } catch (error) {
      console.error('Failed to load clinic short name:', error);
    }
  };

  const handleSaveShortName = async () => {
    try {
      const token = localStorage.getItem('auth_token');
      const userResponse = await fetch(`${API_URL}/api/users/me`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (userResponse.ok) {
        const userData = await userResponse.json();
        if (userData.clinic_id) {
          const response = await fetch(`${API_URL}/api/clinics/${userData.clinic_id}`, {
            method: 'PUT',
            headers: {
              'Authorization': `Bearer ${token}`,
              'Content-Type': 'application/json'
            },
            body: JSON.stringify({ sms_short_name: clinicShortName })
          });

          if (response.ok) {
            toast.success('Clinic short name updated successfully');
            setEditingShortName(false);
          } else {
            toast.error('Failed to update clinic short name');
          }
        }
      }
    } catch (error) {
      console.error('Error updating clinic short name:', error);
      toast.error('Failed to update clinic short name');
    }
  };


  const loadPatientGroups = async () => {
    setLoadingGroups(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/sms/patient-groups`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setPatientGroups(data);
      }
    } catch (error) {
      console.error('Failed to load patient groups:', error);
    } finally {
      setLoadingGroups(false);
    }
  };

  const loadAllPatients = async () => {
    setLoadingPatients(true);
    try {
      const token = localStorage.getItem('auth_token');
      const response = await fetch(`${API_URL}/api/patients`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        const data = await response.json();
        setAllPatients(data);
      }
    } catch (error) {
      console.error('Failed to load patients:', error);
      toast.error('Failed to load patients');
    } finally {
      setLoadingPatients(false);
    }
  };

  const handleSelectAll = (checked) => {
    if (checked) {
      const allIds = filteredPatients.map(p => p.id);
      setSelectedPatientIds(allIds);
    } else {
      setSelectedPatientIds([]);
    }
  };

  const handlePatientToggle = (patientId) => {
    setSelectedPatientIds(prev => {
      if (prev.includes(patientId)) {
        return prev.filter(id => id !== patientId);
      } else {
        return [...prev, patientId];
      }
    });
  };

  const filteredPatients = allPatients.filter(patient => {
    if (!searchQuery) return true;
    const query = searchQuery.toLowerCase();
    return (
      patient.full_name?.toLowerCase().includes(query) ||
      patient.phone?.includes(query) ||
      patient.email?.toLowerCase().includes(query)
    );
  });

  const insertNamePlaceholder = () => {
    const cursorPosition = document.getElementById('message')?.selectionStart || message.length;
    const newMessage = message.slice(0, cursorPosition) + '{{NAME}}' + message.slice(cursorPosition);
    setMessage(newMessage);
  };

  const calculateSmsCount = () => {
    if (!message) return 0;
    
    // Get full message with footer if enabled
    const fullMessage = getFullMessageWithFooter();
    
    const charLimit = encoding === 'plain' ? 160 : 70;
    return Math.ceil(fullMessage.length / charLimit);
  };

  const getFullMessageWithFooter = () => {
    if (!message) return '';
    
    if (appendFooter && clinicShortName) {
      return `${message}\n- ${clinicShortName}`;
    }
    
    return message;
  };

  const getCharLimit = () => {
    return encoding === 'plain' ? 160 : 70;
  };

  const handleSendClick = () => {
    if (!message.trim()) {
      setAlertMessage('Please enter a message');
      setAlertType('error');
      return;
    }

    if (!campaignName.trim()) {
      setAlertMessage('Please enter a campaign name');
      setAlertType('error');
      return;
    }

    if (selectionMode === 'individual' && selectedPatientIds.length === 0) {
      setAlertMessage('Please select at least one patient');
      setAlertType('error');
      return;
    }

    const smsCount = calculateSmsCount();
    const recipientCount = selectionMode === 'individual' ? selectedPatientIds.length : '(based on group)';
    
    setPreviewData({
      message: message,
      encoding: encoding,
      campaignName: campaignName,
      selectionMode: selectionMode,
      patientGroup: selectedGroup,
      selectedCount: selectionMode === 'individual' ? selectedPatientIds.length : null,
      smsCountPerRecipient: smsCount
    });
    
    setShowConfirmDialog(true);
  };

  const handleConfirmSend = async () => {
    setLoading(true);
    setAlertMessage('');
    setShowConfirmDialog(false);

    try {
      const token = localStorage.getItem('auth_token');
      
      // Get full message with footer
      const fullMessage = getFullMessageWithFooter();
      
      const requestBody = {
        content: fullMessage,
        encoding: encoding,
        campaign_name: campaignName
      };

      // Add selection criteria based on mode
      if (selectionMode === 'individual') {
        requestBody.patient_ids = selectedPatientIds;
      } else {
        requestBody.patient_group = selectedGroup;
      }
      
      const response = await fetch(`${API_URL}/api/sms/bulk-send`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(requestBody)
      });

      const data = await response.json();

      if (response.ok) {
        setAlertMessage(
          `✓ Campaign sent successfully! Sent: ${data.sent_count}, Failed: ${data.failed_count || 0}, Credits used: ${data.total_credits_used}`
        );
        setAlertType('success');
        setMessage('');
        setCampaignName('');
        setSelectedPatientIds([]);
        loadCredits();
      } else {
        setAlertMessage(data.detail || 'Failed to send SMS campaign');
        setAlertType('error');
      }
    } catch (error) {
      console.error('Error sending SMS campaign:', error);
      setAlertMessage('Failed to send SMS campaign');
      setAlertType('error');
    } finally {
      setLoading(false);
    }
  };

  const charCount = getFullMessageWithFooter().length;
  const charLimit = getCharLimit();
  const smsCount = calculateSmsCount();
  const estimatedCost = smsCount; // 1 SMS = 1 credit per recipient

  return (
    <Layout>
      <div className="max-w-4xl mx-auto px-4 py-8 space-y-6">
        <h1 className="text-3xl font-bold" style={{ color: "#652D90" }}>Marketing SMS</h1>

      {alertMessage && (
        <Alert className={alertType === 'success' ? 'border-green-600 bg-green-50' : 'border-red-600 bg-red-50'}>
          <AlertDescription>{alertMessage}</AlertDescription>
        </Alert>
      )}

      <Card>
        <CardHeader>
          <CardTitle>Create SMS Campaign</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Campaign Name */}
          <div className="space-y-2">
            <Label htmlFor="campaignName">Campaign Name</Label>
            <Input
              id="campaignName"
              value={campaignName}
              onChange={(e) => setCampaignName(e.target.value)}
              placeholder="e.g., Health Checkup Reminder"
            />
          </div>

          {/* Selection Mode */}
          <div className="space-y-2">
            <Label>Recipient Selection Mode</Label>
            <div className="flex gap-4">
              <button
                onClick={() => setSelectionMode('group')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  selectionMode === 'group' 
                    ? 'border-purple-600 bg-purple-50 text-purple-900' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <Users className="h-5 w-5 mx-auto mb-1" />
                <div className="text-sm font-medium">Patient Groups</div>
                <div className="text-xs text-gray-500">By medical history tags</div>
              </button>
              <button
                onClick={() => setSelectionMode('individual')}
                className={`flex-1 p-3 rounded-lg border-2 transition-all ${
                  selectionMode === 'individual' 
                    ? 'border-purple-600 bg-purple-50 text-purple-900' 
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <UserCheck className="h-5 w-5 mx-auto mb-1" />
                <div className="text-sm font-medium">Select Individuals</div>
                <div className="text-xs text-gray-500">Choose specific patients</div>
              </button>
            </div>
          </div>

          {/* Group Selection (when mode is 'group') */}
          {selectionMode === 'group' && (
            <div className="space-y-2">
              <Label htmlFor="patientGroup">Target Patient Group</Label>
              <Select value={selectedGroup} onValueChange={setSelectedGroup}>
                <SelectTrigger id="patientGroup">
                  <SelectValue placeholder="Select patient group" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Patients</SelectItem>
                  {patientGroups.map((group) => (
                    <SelectItem key={group} value={group}>
                      {group}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {loadingGroups && <p className="text-sm text-gray-500">Loading patient groups...</p>}
            </div>
          )}

          {/* Individual Patient Selection */}
          {selectionMode === 'individual' && (
            <div className="space-y-3 border rounded-lg p-4 bg-gray-50">
              <div className="flex justify-between items-center">
                <Label>Select Patients ({selectedPatientIds.length} selected)</Label>
                {filteredPatients.length > 0 && (
                  <div className="flex items-center gap-2">
                    <Checkbox
                      id="selectAll"
                      checked={selectedPatientIds.length === filteredPatients.length && filteredPatients.length > 0}
                      onCheckedChange={handleSelectAll}
                    />
                    <label htmlFor="selectAll" className="text-sm font-medium cursor-pointer">
                      Select All ({filteredPatients.length})
                    </label>
                  </div>
                )}
              </div>

              {/* Search Box */}
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                <Input
                  type="text"
                  placeholder="Search by name, phone, or email..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10"
                />
              </div>

              {/* Patient List */}
              {loadingPatients ? (
                <div className="text-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin mx-auto text-purple-600" />
                  <p className="text-sm text-gray-500 mt-2">Loading patients...</p>
                </div>
              ) : filteredPatients.length === 0 ? (
                <div className="text-center py-8 text-gray-500">
                  {searchQuery ? 'No patients found matching your search' : 'No patients available'}
                </div>
              ) : (
                <div className="max-h-64 overflow-y-auto space-y-2 bg-white rounded border p-2">
                  {filteredPatients.map((patient) => (
                    <div
                      key={patient.id}
                      className={`flex items-center gap-3 p-2 rounded hover:bg-gray-50 cursor-pointer ${
                        selectedPatientIds.includes(patient.id) ? 'bg-purple-50' : ''
                      }`}
                      onClick={() => handlePatientToggle(patient.id)}
                    >
                      <Checkbox
                        checked={selectedPatientIds.includes(patient.id)}
                        onCheckedChange={() => handlePatientToggle(patient.id)}
                      />
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm">{patient.full_name}</div>
                        <div className="text-xs text-gray-500 truncate">
                          {patient.phone} {patient.email && `• ${patient.email}`}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
          )}

          {/* Encoding Type */}
          <div className="space-y-2">
            <Label htmlFor="encoding">Message Type</Label>
            <Select value={encoding} onValueChange={setEncoding}>
              <SelectTrigger id="encoding">
                <SelectValue placeholder="Select encoding" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="plain">Normal Text (160 chars/SMS)</SelectItem>
                <SelectItem value="unicode">Unicode/Nepali (70 chars/SMS)</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Clinic Short Name Footer Settings */}
          <div className="space-y-3 p-4 border rounded-lg bg-gray-50">
            <div className="flex items-center justify-between">
              <Label className="font-semibold">SMS Footer Settings</Label>
              {!editingShortName && clinicShortName && (
                <Button
                  onClick={() => setEditingShortName(true)}
                  variant="outline"
                  size="sm"
                >
                  Edit Short Name
                </Button>
              )}
            </div>

            {editingShortName || !clinicShortName ? (
              <div className="space-y-2">
                <Label htmlFor="shortName">Clinic Short Name for SMS Footer</Label>
                <div className="flex gap-2">
                  <Input
                    id="shortName"
                    value={clinicShortName}
                    onChange={(e) => setClinicShortName(e.target.value)}
                    placeholder="e.g., HYA Clinic, ABC Hospital"
                    maxLength={30}
                  />
                  <Button onClick={handleSaveShortName} size="sm">
                    Save
                  </Button>
                  {clinicShortName && (
                    <Button onClick={() => setEditingShortName(false)} variant="outline" size="sm">
                      Cancel
                    </Button>
                  )}
                </div>
                <p className="text-xs text-gray-500">
                  This name will be appended as footer in all SMS (max 30 chars)
                </p>
              </div>
            ) : (
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="appendFooter"
                  checked={appendFooter}
                  onChange={(e) => setAppendFooter(e.target.checked)}
                  className="cursor-pointer"
                />
                <Label htmlFor="appendFooter" className="cursor-pointer">
                  Append footer: "- {clinicShortName}"
                </Label>
              </div>
            )}
            
            {appendFooter && clinicShortName && (
              <div className="text-xs text-gray-600 mt-2 p-2 bg-white rounded border">
                <strong>Footer preview:</strong> - {clinicShortName} ({clinicShortName.length + 3} chars)
              </div>
            )}
          </div>

          {/* Message */}
          <div className="space-y-2">
            <div className="flex justify-between items-center">
              <Label htmlFor="message">Message Content</Label>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={insertNamePlaceholder}
                className="text-xs"
              >
                Insert {"{{NAME}}"}
              </Button>
            </div>
            <Textarea
              id="message"
              value={message}
              onChange={(e) => setMessage(e.target.value)}
              placeholder="Type your message here... Use {{NAME}} to personalize with patient name"
              rows={6}
            />
            <div className="flex justify-between text-sm">
              <span className={charCount > charLimit ? 'text-orange-600 font-medium' : 'text-gray-600'}>
                {charCount} / {charLimit} characters
              </span>
              <span className="text-gray-600">
                SMS Count: <strong>{smsCount}</strong> ({estimatedCost} credit{estimatedCost !== 1 ? 's' : ''} per recipient)
              </span>
            </div>
            <Alert className="bg-blue-50 border-blue-200">
              <AlertDescription className="text-xs text-blue-900">
                <strong>💡 Tip:</strong> Use <code className="bg-blue-100 px-1 rounded">{"{{NAME}}"}</code> in your message to automatically replace it with each patient's name. 
                Example: "Dear {"{{NAME}}"}, your appointment is confirmed."
              </AlertDescription>
            </Alert>
          </div>

          <Button
            onClick={handleSendClick}
            disabled={loading || !message.trim() || !campaignName.trim()}
            className="w-full"
          >
            {loading ? (
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
            ) : (
              <Send className="mr-2 h-4 w-4" />
            )}
            Send Campaign
          </Button>
        </CardContent>
      </Card>

      {/* Confirmation Dialog */}
      <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <AlertTriangle className="h-5 w-5 text-orange-600" />
              Confirm SMS Campaign
            </DialogTitle>
          </DialogHeader>
          
          {previewData && (
            <div className="space-y-3">
              <div className="p-4 bg-gray-50 rounded-lg space-y-2">
                <p><strong>Campaign:</strong> {previewData.campaignName}</p>
                {previewData.selectionMode === 'individual' ? (
                  <p><strong>Selected Patients:</strong> {previewData.selectedCount} patients</p>
                ) : (
                  <p><strong>Target Group:</strong> {previewData.patientGroup === 'all' ? 'All Patients' : previewData.patientGroup}</p>
                )}
                <p><strong>Encoding:</strong> {previewData.encoding === 'plain' ? 'Normal Text' : 'Unicode/Nepali'}</p>
                <p><strong>SMS per recipient:</strong> {previewData.smsCountPerRecipient}</p>
                <div className="mt-2">
                  <strong>Message:</strong>
                  <p className="mt-1 p-2 bg-white rounded border text-sm whitespace-pre-wrap">
                    {previewData.message}
                  </p>
                  {previewData.message.includes('{{NAME}}') && (
                    <p className="text-xs text-blue-600 mt-1">
                      ℹ️ {"{{NAME}}"} will be replaced with each patient's name
                    </p>
                  )}
                </div>
              </div>

              <Alert>
                <AlertDescription>
                  <strong>Note:</strong> Credits will be deducted only for successfully delivered messages. 
                  Invalid phone numbers will be automatically discarded.
                </AlertDescription>
              </Alert>
            </div>
          )}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowConfirmDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleConfirmSend} disabled={loading}>
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Send className="mr-2 h-4 w-4" />}
              Confirm & Send
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Usage Guide */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Marketing SMS Guidelines</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2 text-sm text-gray-600">
          <p><strong>Character Limits:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Normal Text: 160 characters = 1 SMS</li>
            <li>Unicode/Nepali: 70 characters = 1 SMS</li>
          </ul>
          <p className="mt-3"><strong>Patient Groups:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Groups are based on medical history tags (e.g., "Diabetics", "High BP")</li>
            <li>Select "All Patients" to send to everyone in your clinic</li>
          </ul>
          <p className="mt-3"><strong>Credit Usage:</strong></p>
          <ul className="list-disc list-inside ml-4 space-y-1">
            <li>Credits are deducted only for successfully delivered messages</li>
            <li>Invalid phone numbers are automatically filtered out</li>
            <li>Each SMS segment costs 1 credit per recipient</li>
          </ul>
        </CardContent>
      </Card>
      </div>
    </Layout>
  );
}
